'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import type { Result } from 'components/Search/Listing/utils/retrieveSearch';

interface UseSearchAutocompleteOptions {
  debounceMs?: number;
  minChars?: number;
}

interface UseSearchAutocompleteReturn {
  results: Result[];
  loading: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  setInputValueQuiet: (value: string) => void;
  clearResults: () => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const useSearchAutocomplete = (options: UseSearchAutocompleteOptions = {}): UseSearchAutocompleteReturn => {
  const { debounceMs = 300, minChars = 2 } = options;
  const [inputValueRaw, setInputValueRaw] = useState('');
  const [debouncedValue] = useDebounce(inputValueRaw, debounceMs);
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const inputValue = inputValueRaw;

  const setInputValue = useCallback((value: string) => {
    setInputValueRaw(value);
    setIsOpen(true);
  }, []);

  const setInputValueQuiet = useCallback((value: string) => {
    setInputValueRaw(value);
  }, []);

  const clearResults = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setResults([]);
    setLoading(false);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      if (debouncedValue.length < minChars) {
        setResults([]);
        setLoading(false);

        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      try {
        const res = await fetch(`/api/elastic?search=${encodeURIComponent(debouncedValue)}`, {
          signal: controller.signal,
        });
        if (!res.ok) {
          setResults([]);

          return;
        }
        const data = await res.json();
        setResults(data.results || []);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        setResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [debouncedValue, minChars]);

  return { results, loading, inputValue, setInputValue, setInputValueQuiet, clearResults, isOpen, setIsOpen };
};
